import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import { Row, Col, Label, FormGroup } from "reactstrap";
import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDriverCreditEnums } from "../../../../redux/slices/driverCreditSlice";
import { useForm, useController } from "react-hook-form";
import CustomSelect from "../../../../CommonElements/CustomSelect";
import {
  createPackageDriverCreditMapping,
  updatePackageDriverCreditMapping,
  getAllDriverCredits,
} from "../../../../Services/DriverCredit.service";
import { showAlert } from "../../../../Services/Shared.service";

const CreateEditDriverCreditForm = ({ open, onClose, onSuccess, editData, packId, corporateClientId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [driverCredits, setDriverCredits] = useState([]);

  // Enums come from Redux (cached after first fetch)
  const creditTypesObj = useSelector((state) => state.driverCredit.creditTypes);
  const calculateTypesObj = useSelector((state) => state.driverCredit.calculateTypes);
  const enumsLoading = useSelector((state) => state.driverCredit.loading);

  // Map local data for dropdowns
  const driverCreditOptions = useMemo(() => {
    return driverCredits.map((credit) => ({
      value: credit.id,
      label: credit.category,
    }));
  }, [driverCredits]);

  const creditTypeOptions = useMemo(() => {
    return Object.entries(creditTypesObj).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [creditTypesObj]);

  const creditSourceOptions = useMemo(() => {
    return Object.entries(calculateTypesObj).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [calculateTypesObj]);

  // None option — used when Amount is selected
  const noneOption = useMemo(
    () => creditSourceOptions.find(
      (opt) => opt.label?.toLowerCase() === "none" || opt.value?.toUpperCase() === "NONE"
    ) || null,
    [creditSourceOptions]
  );

  // Percentage options — exclude None and WithAddCharge
  const percentageSourceOptions = useMemo(
    () => creditSourceOptions.filter((opt) => {
      const lbl = opt.label?.toLowerCase() ?? '';
      const val = opt.value?.toLowerCase() ?? '';
      return lbl !== "none" &&
             val !== "none" &&
             !lbl.includes("withadd") &&
             !lbl.includes("with add") &&
             !lbl.replace(/\s/g, '').includes("withaddcharge") &&
             !val.includes("withadd");
    }),
    [creditSourceOptions]
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      driverCreditId: null,
      creditSource: null,
      creditType: null,
      creditValue: "",
    },
  });

  const driverCreditIdField = useController({
    name: "driverCreditId",
    control,
    rules: { required: { value: true, message: "Driver Credit is required" } },
  }).field;

  const creditSourceField = useController({
    name: "creditSource",
    control,
    rules: { required: { value: true, message: "Credit Source is required" } },
  }).field;

  const creditTypeField = useController({
    name: "creditType",
    control,
    rules: { required: { value: true, message: "Credit Type is required" } },
  }).field;

  const selectedCreditType = watch("creditType");
  const isAmountType = selectedCreditType?.value === "A";

  // When Credit Type = Amount, force Credit Source to NONE; otherwise clear it
  useEffect(() => {
    if (!open) return;
    if (isAmountType) {
      setValue("creditSource", noneOption);
    } else {
      // Clear if previously force-set to None when switching away from Amount
      const currentSource = getValues("creditSource");
      const isNone =
        currentSource?.label?.toLowerCase() === "none" ||
        currentSource?.value?.toUpperCase() === "NONE";
      if (isNone) {
        setValue("creditSource", null);
      }
    }
  }, [isAmountType, open, noneOption, setValue, getValues]);

  const isEdit = Boolean(editData);

  // Fetch driverCredits locally each open (changes more often).
  // Enums dispatched to Redux — cached after the first call
  useEffect(() => {
    if (!open) return;
    getAllDriverCredits({ isPagination: false })
      .then((resp) => setDriverCredits(resp?.data?.list || resp?.data || []))
      .catch(() => showAlert("error", "Failed to load driver credits"));
    dispatch(fetchDriverCreditEnums());
  }, [open, dispatch]);

  // Populate form when editing or reset for create
  useEffect(() => {
    if (!open) return;

    if (editData && driverCreditOptions.length > 0 && creditTypeOptions.length > 0) {
      const selectedCredit = driverCreditOptions.find(
        (opt) => opt.value === editData.driverCreditId
      );
      const selectedType = creditTypeOptions.find(
        (opt) => opt.value === editData.creditType
      );
      const selectedSource = creditSourceOptions.find(
        (opt) => opt.value === editData.creditSource
      );
      reset({
        driverCreditId: selectedCredit || null,
        creditSource: selectedSource || null,
        creditType: selectedType || null,
        creditValue: editData.creditValue ?? "",
      });
    } else if (!editData) {
      reset({
        driverCreditId: null,
        creditSource: null,
        creditType: null,
        creditValue: "",
      });
    }
  }, [editData, driverCreditOptions, creditTypeOptions, reset, open]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const idField = isEdit
        ? (editData.corporateClientId
            ? { corporateClientId: editData.corporateClientId }
            : { packagesId: editData.packagesId })
        : (corporateClientId
            ? { corporateClientId: Number(corporateClientId) }
            : { packagesId: Number(packId) });

      const payload = {
        ...idField,
        driverCreditId: data.driverCreditId?.value,
        creditSource: data.creditSource?.value,
        creditType: data.creditType?.value,
        creditValue: parseFloat(data.creditValue),
      };

      if (isEdit) {
        await updatePackageDriverCreditMapping(editData.id, payload);
        showAlert("success", "Driver credit updated successfully");
      } else {
        await createPackageDriverCreditMapping(payload);
        showAlert("success", "Driver credit created successfully");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving driver credit:", error);
      showAlert(
        "error",
        error.response?.data?.message || "Failed to save driver credit"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
          overflow: "visible",
        },
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2, fontWeight: 600, fontSize: "1.25rem" }}>
        {isEdit ? "Edit Driver Credit" : "Create Driver Credit"}
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1, overflow: "visible" }}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ minHeight: "200px" }}
        >
          <Row>
            <FormGroup className="col-lg-6">
              <Label className="col-lg-12 col-form-label">
                Driver Credit <span className="text-danger">*</span>
              </Label>
              <Col lg="12">
                <CustomSelect
                  className="service-select"
                  classNamePrefix="select"
                  isClearable={true}
                  isSearchable={true}
                  value={driverCreditIdField.value}
                  onChange={(selected) => driverCreditIdField.onChange(selected)}
                  options={driverCreditOptions}
                  placeholder="Select Driver Credit"
                  maxMenuHeight={150}
                />
                <span style={{ color: "red" }}>{errors?.driverCreditId?.message}</span>
              </Col>
            </FormGroup>

            <FormGroup className="col-lg-6">
              <Label className="col-lg-12 col-form-label">
                Credit Type <span className="text-danger">*</span>
              </Label>
              <Col lg="12">
                <CustomSelect
                  className="service-select"
                  classNamePrefix="select"
                  isClearable={true}
                  isSearchable={true}
                  value={creditTypeField.value}
                  onChange={(selected) => {
                    creditTypeField.onChange(selected);
                    // Only re-validate creditValue if the user has already interacted with it.
                    // Calling trigger() unconditionally puts creditValue into RHF's "validated"
                    // set, causing it to re-validate on every subsequent field change (e.g.
                    // creditSource), which is what was causing the premature error display.
                    if (touchedFields.creditValue || dirtyFields.creditValue) {
                      trigger("creditValue");
                    }
                  }}
                  isLoading={enumsLoading}
                  options={creditTypeOptions}
                  placeholder="Select Credit Type"
                  maxMenuHeight={150}
                />
                <span style={{ color: "red" }}>{errors?.creditType?.message}</span>
              </Col>
            </FormGroup>

          </Row>

          <Row>

            <FormGroup className="col-lg-6">
              <Label className="col-lg-12 col-form-label">
                Credit Source <span className="text-danger">*</span>
              </Label>
              <Col lg="12">
                <CustomSelect
                  className="service-select"
                  classNamePrefix="select"
                  isClearable={!isAmountType}
                  isSearchable={true}
                  value={creditSourceField.value}
                  onChange={(selected) => creditSourceField.onChange(selected)}
                  isLoading={enumsLoading}
                  options={isAmountType ? [] : percentageSourceOptions}
                  placeholder={isAmountType ? "None (auto)" : "Select Credit Source"}
                  isDisabled={isAmountType}
                  maxMenuHeight={150}
                />
                <span style={{ color: "red" }}>{errors?.creditSource?.message}</span>
              </Col>
            </FormGroup>

            <FormGroup className="col-lg-6">
              <Label className="col-lg-12 col-form-label">
                Credit Value <span className="text-danger">*</span>
              </Label>
              <Col lg="12">
                <input
                  className="form-control"
                  type="number"
                  placeholder="Enter Credit Value"
                  {...register("creditValue", {
                    required: { value: true, message: "Credit Value is required" },
                    min: {
                      value: 0,
                      message: "Credit Value cannot be negative",
                    },
                    validate: (value) => {
                      const creditType = getValues("creditType"); // always fresh value
                      if (creditType?.value === "P" && value > 100) {
                        return "Percentage cannot be greater than 100";
                      }
                      if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                        return "Invalid value format";
                      }
                      return true;
                    }
                  })}
                />
                <span style={{ color: "red" }}>{errors?.creditValue?.message}</span>
              </Col>
            </FormGroup>
          </Row>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0, justifyContent: "flex-end", gap: 1 }}>
        <button
          onClick={onClose}
          disabled={loading}
          type="button"
          className="btn"
          style={{
            backgroundColor: "white",
            color: "#666",
            border: "1px solid #d9d9d9",
            padding: "8px 20px",
            borderRadius: "6px",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit(onSubmit)}
          disabled={loading}
          type="button"
          className="btn"
          style={{
            backgroundColor: "rgba(57, 181, 74, 1)",
            color: "white",
            border: "none",
            padding: "8px 30px",
            borderRadius: "6px",
          }}
        >
          {loading
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
            ? "Update"
            : "Create"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditDriverCreditForm;
